import { dbCreateMap, dbUpdateMap, dbDeleteMap, dbFindDuplicateMap, dbFetchMapMetadata } from '@/api/maps';
import { MapData } from './types/MapData';
import { BirthChartData } from '@/types/birthChart';
import { CalculationInput, CalculationResult } from '@/types/Calculator';
import { SupabaseClient } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

import { formatForStorage } from '@/utils/dates';
import { normalizeText } from './mapTransforms';
import { isVirtualId } from './virtualMaps';
// checkForDuplicateMap is now in dbFindDuplicateMap

/**
 * Maps the flat CalculationResult/Numerology structure to database columns
 */
export const getFlattenedFields = (result: CalculationResult) => ({
    // Calculation fields
    inner_sign_number: result.innerSign.number,
    inner_sign_name: result.innerSign.sign,
    age: result.innerSign.age || null,
    surrounding_sign_number: result.surroundingSign.number,
    surrounding_sign_name: result.surroundingSign.sign,
    gematria_value: result.surroundingSign.gematria,
    profile_number: result.profile.profileNumber,
    profile_sign: result.profile.profileSign,
    zodiac_position: result.profile.zodiacPosition,
    tur_value: result.tur.value,
    tur_label: result.tur.label,

    // Numerology fields
    day_number: result.numerology.dayNumber,
    month_number: result.numerology.monthNumber,
    year_number: result.numerology.yearNumber,
    total_number: result.numerology.totalNumber,
    total_from_all_digits: result.numerology.totalFromAllDigits,
    first_number: result.numerology.firstNumber,
    family_number: result.numerology.familyNumber,
    total_name_number: result.numerology.totalNameNumber,
    ehevi_number: result.numerology.eheviNumber,
    itzurim_number: result.numerology.itzurimNumber,
    ehevi_inner_sum_first: result.numerology.eheviInnerSumFirst,
    ehevi_inner_sum_family: result.numerology.eheviInnerSumFamily,
    itzurim_inner_sum_first: result.numerology.itzurimInnerSumFirst,
    itzurim_inner_sum_family: result.numerology.itzurimInnerSumFamily,
    full_inner_sum: result.numerology.fullInnerSum,
    ehevi_inner_sum: result.numerology.eheviInnerSum,
    itzurim_inner_sum: result.numerology.itzurimInnerSum,
    full2_inner_sum: result.numerology.full2InnerSum,
    outer_sum: result.numerology.hamashaOuterSum,
    hamasha_inner_sum: result.numerology.hamashaInnerSum,
    astrology_cache: result.astrologyCache ? (result.astrologyCache as BirthChartData) : null
});

/**
 * Create a new map with flattened data.
 * Checks for duplicates before creating: if a duplicate exists (same identifiers),
 * it merges the new data into the existing map.
 */
export const createMap = async (
    userId: string,
    input: CalculationInput,
    result: CalculationResult,
    notes: string | null,
    labels: string[] = [],
    client?: SupabaseClient,
    bypassDuplicateCheck = false,
    gender?: string | null,
    soulRoot?: string | null
): Promise<{ id: string; isMerged: boolean }> => {
    // 1. Normalize identifiers
    const normalizedFirstName = normalizeText(input.firstName)!;
    const normalizedFamilyName = normalizeText(input.familyName);
    const normalizedMotherName = normalizeText(input.motherName);
    const normalizedBirthDate = formatForStorage(input.birthDate);
    const normalizedNotes = normalizeText(notes);

    // 2. Check for duplicate
    const existingMapId = bypassDuplicateCheck
        ? null
        : await dbFindDuplicateMap({
            userId,
            firstName: normalizedFirstName,
            familyName: normalizedFamilyName,
            motherName: normalizedMotherName,
            birthDate: normalizedBirthDate,
        }, client);

    const flattenedFields = getFlattenedFields(result);

    if (existingMapId) {
        // --- MERGE LOGIC ---
        // --- MERGE LOGIC ---
        const existingMap = await dbFetchMapMetadata(existingMapId, client);


        // Merge Labels (Union)
        const existingLabels = existingMap?.labels || [];
        const mergedLabels = Array.from(new Set([...existingLabels, ...labels]));

        // Merge Notes (Append if new)
        let mergedNotes = existingMap?.notes || '';
        if (normalizedNotes && !mergedNotes.includes(normalizedNotes)) {
            mergedNotes = mergedNotes ? `${mergedNotes}\n\n${normalizedNotes}` : normalizedNotes;
        }

        // Update the map with flattened fields + merged metadata
        await updateMap(existingMapId, {
            firstName: normalizedFirstName,
            familyName: normalizedFamilyName,
            motherName: normalizedMotherName,
            birthDate: normalizedBirthDate,
            notes: mergedNotes,
            labels: mergedLabels,
            ...flattenedFields,
            birth_time: input.birthTime || existingMap?.birth_time || null,
            birth_location: input.cityName || existingMap?.birth_location || null,
            birth_lat: input.lat || existingMap?.birth_lat || null,
            birth_lon: input.lng || existingMap?.birth_lon || null,
            timezone: input.timezone || existingMap?.timezone || null,
            gender: gender || input.gender || existingMap?.gender || null,
            soul_root: soulRoot || input.soul_root || existingMap?.soul_root || null,
            astrology_cache: flattenedFields.astrology_cache || existingMap?.astrology_cache || null,
        }, client);

        return { id: existingMapId, isMerged: true };
    }

    // --- CREATE NEW MAP ---
    try {
        const newMap = await dbCreateMap({
            user_id: userId,
            firstName: normalizedFirstName,
            familyName: normalizedFamilyName,
            motherName: normalizedMotherName,
            birthDate: normalizedBirthDate,
            notes: normalizedNotes,
            labels: labels,
            ...flattenedFields,
            birth_time: input.birthTime || null,
            birth_location: input.cityName || null,
            birth_lat: input.lat || null,
            birth_lon: input.lng || null,
            timezone: input.timezone || null,
            gender: gender || input.gender || null,
            soul_root: soulRoot || input.soul_root || null,
        }, client);
        return { id: newMap.id, isMerged: false };
    } catch (mapsError: unknown) {
        logger.error('Error creating map', mapsError, { operation: 'create_map', table: 'maps' });
        throw mapsError;
    }
};

/**
 * Update a map in the flattened 'maps' table.
 */
export const updateMap = async (id: string, updates: Partial<MapData>, client?: SupabaseClient) => {
    if (!id || isVirtualId(id) || id.startsWith('sample-')) {
        return;
    }
    try {
        const birthFields: (keyof MapData)[] = ['birthDate', 'birth_time', 'birth_location', 'birth_lat', 'birth_lon', 'timezone'];
        const hasBirthField = birthFields.some(field => field in updates);

        const finalUpdates = { ...updates };

        if (hasBirthField) {
            // Fetch existing map to verify if fields actually changed
            const existingMap = await dbFetchMapMetadata(id, client);
            if (existingMap) {
                let isChanged = false;
                for (const field of birthFields) {
                    if (field in updates) {
                        const newVal = updates[field];
                        const oldVal = existingMap[field];
                        
                        if (field === 'birthDate') {
                            const normNew = formatForStorage(String(newVal || ''));
                            const normOld = formatForStorage(String(oldVal || ''));
                            if (normNew !== normOld) {
                                isChanged = true;
                                break;
                            }
                        } else {
                            // Perform clean string-normalized comparison
                            const cleanNew = newVal === undefined || newVal === null ? '' : String(newVal).trim();
                            const cleanOld = oldVal === undefined || oldVal === null ? '' : String(oldVal).trim();
                            
                            if (cleanNew !== cleanOld) {
                                isChanged = true;
                                break;
                            }
                        }
                    }
                }
                
                if (isChanged && !('astrology_cache' in updates)) {
                    // Set cache to null to invalidate only if birth coordinates truly changed
                    finalUpdates.astrology_cache = null;
                }
            }
        }

        await dbUpdateMap(id, finalUpdates, client);
    } catch (error: unknown) {
        logger.error('Error updating map', error, { operation: 'update_map', table: 'maps' });
        throw error;
    }
};

export const deleteMap = async (id: string, client?: SupabaseClient) => {
    if (!id || isVirtualId(id) || id.startsWith('sample-')) {
        return;
    }
    try {
        await dbDeleteMap(id, client);
    } catch (error: unknown) {
        logger.error('Error deleting map', error, { operation: 'delete_map', table: 'maps' });
        throw error;
    }
};
